<template>
	<div class="map-section" v-loading="loading">
		<div class="section-header">
			<span class="section-title">调压器分布地图</span>
		</div>
		<div class="map-wrapper">
			<div id="voltage-monitor-map" class="map-container"></div>
		</div>
	</div>
</template>

<script>
import AMapLoader from '@amap/amap-jsapi-loader'
import { AMapKey } from '@/config'
import { apiGetPressureRegulatorList } from '@/apis/pressureRegulatorManage.api'

export default {
	name: 'MapSection',
	data() {
		return {
			AMap: null,
			map: null,
			infoWindow: null,
			loading: false,
			markers: [],
		}
	},
	mounted() {
		this.initMap()
		this.getMapDevices()
	},
	beforeDestroy() {
		if (this.map) {
			this.map.destroy()
			this.map = null
		}
	},
	methods: {
		initMap() {
			AMapLoader.load({
				key: AMapKey,
				version: '2.0',
				plugins: ['AMap.Scale', 'AMap.ToolBar', 'AMap.Geolocation', 'AMap.Size', 'AMap.Icon'],
			}).then(AMap => {
				this.AMap = AMap
				this.map = new AMap.Map('voltage-monitor-map', {
					zoom: 12,
					center: [116.397428, 39.90923],
					viewMode: '2D',
				})
				this.map.addControl(new AMap.Scale())
				this.setCurrentPosition()
				this.renderMarkers()
			})
		},
		setCurrentPosition() {
			if (!this.map || !this.AMap?.Geolocation) return
			const geolocation = new this.AMap.Geolocation({
				enableHighAccuracy: true,
				timeout: 8000,
				zoomToAccuracy: false,
				showButton: false,
				showMarker: false,
				showCircle: false,
			})
			this.map.addControl(geolocation)
			geolocation.getCurrentPosition((status, result) => {
				if (status === 'complete' && result.position && !this.markers.length) {
					this.map.setCenter(result.position)
				}
			})
		},
		getMapDevices() {
			this.loading = true
			apiGetPressureRegulatorList({
				current: 1,
				size: 500,
			})
				.then(res => {
					this.markers = (res.records || []).filter(item => item.longitude && item.latitude)
					this.renderMarkers()
				})
				.catch(() => {
					this.markers = []
					this.renderMarkers()
				})
				.finally(() => {
					this.loading = false
				})
		},
		renderMarkers() {
			if (!this.map || !this.AMap) return
			this.map.clearMap()
			const markers = this.markers.map(item => {
				const marker = new this.AMap.Marker({
					position: [Number(item.longitude), Number(item.latitude)],
					icon: this.getMarkerIcon(item),
					title: item.regulatorCode || item.collectorDeviceNo || '',
				})
				marker.on('click', () => {
					const position = marker.getPosition()
					this.map.setCenter(position)
					this.openInfoWindow(item, position)
				})
				return marker
			})
			if (markers.length) {
				this.map.add(markers)
				this.map.setFitView(markers, false, [80, 80, 80, 80])
			}
		},
		getMarkerIcon(item) {
			return new this.AMap.Icon({
				size: new this.AMap.Size(22, 26),
				image: this.getMarkerIconImage(item),
			})
		},
		getMarkerIconImage(item) {
			const status = item.runningState || item.status || item.deviceStatus
			const iconMap = {
				0: 'normal',
				1: 'alarm',
				2: 'fault',
				3: 'offline',
				normal: 'normal',
				alarm: 'alarm',
				fault: 'fault',
				offline: 'offline',
			}
			const iconName = iconMap[status] || 'normal'
			return require(`@/assets/imgs/board/${iconName}.png`)
		},
		openInfoWindow(item, position) {
			if (!this.infoWindow) {
				this.infoWindow = new this.AMap.InfoWindow({
					isCustom: true,
					offset: new this.AMap.Pixel(0, -28),
				})
			}
			const content = document.createElement('div')
			content.className = 'monitor-map-popover'
			content.innerHTML = `
				<button class="monitor-map-popover__close" type="button">×</button>
				<div class="monitor-map-popover__title">${item.regulatorCode || '--'}</div>
				<div class="monitor-map-popover__row">
					<span>安装地址</span>
					<strong>${item.installAddress || '--'}</strong>
				</div>
			`
			content.querySelector('.monitor-map-popover__close').onclick = () => {
				this.infoWindow.close()
			}
			this.infoWindow.setContent(content)
			this.infoWindow.open(this.map, position)
		},
	},
}
</script>

<style lang="scss" scoped>
.map-section {
	background: var(--panel-bg);
	border: 1px solid var(--panel-border);
	border-radius: 12px;
	box-shadow: var(--panel-shadow);
	backdrop-filter: blur(12px);
	overflow: hidden;
	height: 480px;
	display: flex;
	flex-direction: column;
}
.section-header {
	height: 54px;
	padding: 0 24px;
	border-bottom: 1px solid var(--line);
	display: flex;
	align-items: center;
	flex-shrink: 0;
}
.section-title {
	font-size: 17px;
	font-weight: 700;
	color: var(--title);
}
.map-wrapper {
	position: relative;
	height: 0;
	flex: 1;
}
.map-container {
	width: 100%;
	height: 100%;
	overflow: hidden;
}

::v-deep .monitor-map-popover {
	position: relative;
	width: 220px;
	padding: 14px 16px 12px;
	border: 1px solid #dbe5ee;
	border-radius: 8px;
	background: rgba(255, 255, 255, 0.98);
	box-shadow: 0 14px 34px rgba(15, 23, 42, 0.16);
	color: #334155;
}

::v-deep .monitor-map-popover::after {
	position: absolute;
	left: 50%;
	bottom: -7px;
	width: 12px;
	height: 12px;
	content: '';
	border-right: 1px solid #dbe5ee;
	border-bottom: 1px solid #dbe5ee;
	background: #fff;
	transform: translateX(-50%) rotate(45deg);
}

::v-deep .monitor-map-popover__close {
	position: absolute;
	top: 8px;
	right: 8px;
	width: 20px;
	height: 20px;
	padding: 0;
	border: 0;
	border-radius: 50%;
	background: #f1f5f9;
	color: #64748b;
	line-height: 20px;
	text-align: center;
	cursor: pointer;
}

::v-deep .monitor-map-popover__close:hover {
	background: #e2e8f0;
	color: #0f172a;
}

::v-deep .monitor-map-popover__title {
	padding-right: 22px;
	margin-bottom: 10px;
	color: #0f172a;
	font-size: 14px;
	font-weight: 700;
	line-height: 20px;
}

::v-deep .monitor-map-popover__row {
	display: grid;
	grid-template-columns: 52px minmax(0, 1fr);
	gap: 8px;
	font-size: 12px;
	line-height: 18px;
	margin-top: 6px;
}

::v-deep .monitor-map-popover__row span {
	color: #64748b;
}

::v-deep .monitor-map-popover__row strong {
	color: #334155;
	font-weight: 400;
	word-break: break-all;
	white-space: normal;
}
</style>
